import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ApiRequestError } from './api/client';
import { keys } from './api/queries';
import { RouterProvider } from './router';
// Polices HÉBERGÉES PAR L'APPLICATION (et non chargées depuis Google Fonts) :
// chaque visite transmettait sinon l'adresse IP du visiteur à Google, un
// transfert sans base légale selon la CNIL. Seules les graisses utilisées sont
// importées ; chaque fichier ne se télécharge que pour les caractères affichés.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';
import '@fontsource/newsreader/400-italic.css';
// Design system Zygosight (web/src/zygosight-ds/), AVANT toute autre feuille :
// les rôles `--ds-*`, puis les composants `zy-*`.
import './zygosight-ds/tokens.css';
import './zygosight-ds/composants.css';
import './styles/tokens.css';
import { applyStoredTheme } from './theme/theme';

// Avant le premier rendu : sinon la page s'afficherait un instant dans le
// thème du système avant de basculer sur celui que l'utilisateur a choisi.
applyStoredTheme();

// UNE session expirée doit ramener à l'écran de connexion — pas afficher
// « Non authentifié » dans chaque encart de la page.
//
// Le serveur est seul juge de la validité de la session ; un 401 sur N'IMPORTE
// quelle requête est donc la preuve qu'elle a pris fin, quelle que soit la
// requête concernée. On le traite en UN seul endroit, sinon chaque écran devrait
// y penser — et l'un d'eux l'oublierait.
let client: QueryClient | undefined;

// Même logique pour la MAINTENANCE : un 503 `MAINTENANCE`, sur n'importe quelle
// requête ou action, fait relire l'état et la session — l'application laisse
// alors place à la page de maintenance, sans rechargement.
const onMaintenance = (error: unknown) => {
  if (error instanceof ApiRequestError && error.status === 503 && error.code === 'MAINTENANCE') {
    void client?.invalidateQueries({ queryKey: keys.maintenance });
    void client?.invalidateQueries({ queryKey: keys.me });
  }
};

const queryCache = new QueryCache({
  onError: (error) => {
    if (error instanceof ApiRequestError && error.isUnauthenticated) {
      // `undefined` signifierait « pas encore chargé » ; `null` dit
      // explicitement « aucune session ».
      client?.setQueryData(keys.me, null);
    }
    onMaintenance(error);
  },
});

// MODE DÉMO : les compteurs (« 3 études sur 5 ») sont relus après chaque action
// réussie d'un compte de démo, et après un refus `DEMO_LIMIT`. Hors démo, rien.
const isDemo = () => (client?.getQueryData(keys.demoUsage) as { demo?: boolean } | undefined)?.demo === true;
const refreshDemoUsage = () => void client?.invalidateQueries({ queryKey: keys.demoUsage });

client = new QueryClient({
  queryCache,
  mutationCache: new MutationCache({
    onError: (error) => {
      onMaintenance(error);
      if (error instanceof ApiRequestError && error.code === 'DEMO_LIMIT') refreshDemoUsage();
    },
    onSuccess: () => {
      if (isDemo()) refreshDemoUsage();
    },
  }),
  defaultOptions: {
    queries: {
      // Ne pas réessayer une requête refusée : un 401, 403 ou 404 ne devient
      // pas vrai en insistant. On ne réessaie que les pannes réseau et les
      // erreurs serveur, et une seule fois.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Aide au diagnostic en développement uniquement : inspecter l'état réel du
// cache vaut mieux que de le déduire. Absent du bundle de production.
if (import.meta.env.DEV) {
  (globalThis as unknown as { __qc?: QueryClient }).__qc = client;
}

const container = document.getElementById('root');
if (!container) throw new Error('Élément racine #root introuvable dans le document.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <RouterProvider>
        <App />
      </RouterProvider>
    </QueryClientProvider>
  </StrictMode>,
);
